import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  Link,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import { Help as HelpIcon, Error as ErrorIcon } from '@material-ui/icons';
import InfoBlock from '../../integration-wizard/components/common/InfoBlock';
import styles from './IntegrationSettings.module.scss';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const normalizeResultsUrl = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) || trimmed.startsWith('/')) {
    return trimmed;
  }
  return `/${trimmed}`;
};

const validateCssSelector = (value, t) => {
  const trimmed = value.trim();
  if (!trimmed || typeof document === 'undefined') return null;

  try {
    document.createDocumentFragment().querySelector(trimmed);
    return null;
  } catch (error) {
    return t('search.settings.selector.invalid');
  }
};

const ResultsPageIntegrationSettings = ({
  title,
  config,
  saveConfig: saveFn,
  updateViewIntegration,
  onReady,
  onRegisterSave,
  onUpdate,
}) => {
  const localizeMessage = useLocalizedMessage();
  const integration = config?.search?.views?.resultsPage?.integration || {};
  const [url, setUrl] = useState('');
  const [container, setContainer] = useState('');
  const [selectorError, setSelectorError] = useState(null);

  useEffect(() => {
    setUrl(integration.url || '');
    setContainer(integration.container || '');
    setSelectorError(validateCssSelector(integration.container || '', localizeMessage));
  }, [integration.container, integration.url, localizeMessage]);

  const normalizedIntegration = useMemo(() => ({
    url: normalizeResultsUrl(url),
    container: container.trim(),
  }), [container, url]);

  const saveConfig = useCallback(async () => {
    if (!config || !updateViewIntegration || !saveFn) return;

    const validationError = validateCssSelector(container, localizeMessage);
    setSelectorError(validationError);
    if (validationError) return;

    const updatedConfig = updateViewIntegration('resultsPage', normalizedIntegration);
    if (updatedConfig) {
      await saveFn(updatedConfig);
    }
  }, [config, container, localizeMessage, normalizedIntegration, saveFn, updateViewIntegration]);

  useEffect(() => {
    if (onRegisterSave) {
      onRegisterSave(saveConfig);
    }
  }, [onRegisterSave, saveConfig]);

  useEffect(() => {
    if (onReady) onReady(!selectorError);
  }, [onReady, selectorError]);

  const handleUrlChange = (event) => {
    setUrl(event.target.value);
    if (onUpdate) onUpdate();
  };

  const handleContainerChange = (event) => {
    const nextValue = event.target.value;
    setContainer(nextValue);
    setSelectorError(validateCssSelector(nextValue));
    if (onUpdate) onUpdate();
  };

  if (!config) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  const hasContainer = container.trim().length > 0;

  return (
    <Box className={styles.root}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={7}>
          <section className={styles.panel}>
            <div className={styles.panelHeader}>
              <Typography component="h2" className={styles.title}>
                {title || localizeMessage('search.settings.resultsPage.integrationTitle')}
              </Typography>
            </div>

            <Typography component="h3" className={styles.sectionTitle}>
              <IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.urlAndContainer" />
            </Typography>
            <InfoBlock
              title={<IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.whereShouldSearchResultsRender" />}
              description={<IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.chooseTheStorefrontUrlForTheFullResultsPageAnd" />}
              items={[
                <>
                  <IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.useARelativeUrlSuchAs" />{' '}<code>/search</code>{' '}<IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.or" />{' '}<code>/kea-search</code>.
                </>,
                <>
                  <IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.pointTheContainerSelectorToTheProductListWrapperForExample" />{' '}<code>#js-product-list</code>.
                </>,
              ]}
            />

            <div className={styles.fieldGroup}>
              <TextField
                id="kea-results-page-url"
                fullWidth
                variant="outlined"
                label={<IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.resultsPageUrl" />}
                value={url}
                onChange={handleUrlChange}
                placeholder={localizeMessage('search.settings.resultsPage.urlPlaceholder')}
                helperText={<IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.usedWhenShoppersSubmitAFullSearchAndLeaveThe" />}
                inputProps={{ 'data-testid': 'results-page-url-input' }}
              />

              <TextField
                id="kea-results-page-container"
                fullWidth
                required
                variant="outlined"
                label={<IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.resultsContainerSelector" />}
                value={container}
                onChange={handleContainerChange}
                placeholder="#js-product-list"
                error={!!selectorError}
                helperText={selectorError || localizeMessage('search.settings.resultsPage.containerHelper')}
                inputProps={{ 'data-testid': 'results-page-container-input' }}
              />
            </div>

            {!hasContainer && (
              <div className={styles.warning}>
                <ErrorIcon />
                <Typography variant="body2">
                  <IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.addAResultsContainerSelectorBeforeEnablingTheFullResults" />
                </Typography>
              </div>
            )}
          </section>
        </Grid>

        <Grid item xs={12} md={5}>
          <aside className={styles.helpPanel}>
            <div className={styles.helpHeader}>
              <span className={styles.helpIcon}><HelpIcon /></span>
              <Typography component="h2" className={styles.helpTitle}>
                <IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.configuringTheResultsPage" />
              </Typography>
            </div>

            <Typography variant="body1" paragraph>
              <IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.inspectTheStorefrontPageThatShouldContainTheRenderedProduct" />
            </Typography>

            <ul className={styles.helpList}>
              <li><Typography><IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.useAStableSelectorSuchAs" />{' '}<code>#js-product-list</code>{' '}<IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.or" />{' '}<code>.products</code>.</Typography></li>
              <li><Typography><IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.avoidElementsCreatedOnlyAfterOtherJavaScriptHasRendered" /></Typography></li>
              <li><Typography><IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.keepTheUrlStableAcrossPreviewAndLiveBundles" /></Typography></li>
            </ul>

            <div className={styles.reference}>
              <Typography variant="subtitle2" className={styles.referenceTitle}>
                <IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.selectorReference" />
              </Typography>
              <Link
                className={styles.referenceLink}
                href="https://www.w3schools.com/CSS/css_selectors.asp"
                target="_blank"
                rel="noopener"
              >
                <IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.openTheCssSelectorsGuide" />
              </Link>
              <Typography variant="caption" display="block" color="textSecondary">
                <IntlMessages id="ui.searchSettings.resultsPageIntegrationSettings.diagnosticsAlsoUseThisSelectorToVerifyThatTheResults" />
              </Typography>
            </div>
          </aside>
        </Grid>
      </Grid>
    </Box>
  );
};

ResultsPageIntegrationSettings.propTypes = {
  title: PropTypes.node,
  config: PropTypes.shape({}),
  saveConfig: PropTypes.func,
  updateViewIntegration: PropTypes.func,
  onReady: PropTypes.func,
  onRegisterSave: PropTypes.func,
  onUpdate: PropTypes.func,
};

ResultsPageIntegrationSettings.defaultProps = {
  title: null,
  config: null,
  saveConfig: null,
  updateViewIntegration: null,
  onReady: null,
  onRegisterSave: null,
  onUpdate: null,
};

export default ResultsPageIntegrationSettings;
